import { useReducer, useEffect, useCallback } from 'react'
import api, { ILeaseSearchResult } from '@api'
import { debounce } from 'lodash'
import { Toaster } from '@ui'
import { ApolloError } from 'apollo-boost'

enum Actions {
  SEARCH,
  LOAD,
}
interface IState {
  loading: boolean
  query: string
  reqId: number
  items: ILeaseSearchResult[]
}

interface SearchAction {
  type: Actions.SEARCH
  query: string
  reqId: number
}
interface LoadAction {
  type: Actions.LOAD
  items: ILeaseSearchResult[]
  reqId: number
}
type LeaseSearchActionTypes = LoadAction | SearchAction

function reducer(state: IState, action: LeaseSearchActionTypes) {
  switch (action.type) {
    case Actions.SEARCH:
      return {
        ...state,
        query: action.query,
        reqId: action.reqId,
        loading: true,
      }
    case Actions.LOAD:
      return state.reqId > action.reqId
        ? state
        : {
            ...state,
            items: action.items,
            loading: false,
          }
    default:
      return state
  }
}

export default function useLeaseSearch() {
  const [state, dispatch] = useReducer(reducer, {
    loading: false,
    query: '',
    reqId: 0,
    items: [],
  })

  const debouncedSearch = useCallback(
    debounce((query: string, reqId: number) => {
      api.lease
        .search(query)
        .then((items: ILeaseSearchResult[]) => {
          dispatch({ type: Actions.LOAD, reqId, items })
        })
        .catch((error: ApolloError) => Toaster.fail({ error }))
    }, 300),
    []
  )

  useEffect(() => {
    const reqId = Date.now()

    if (!state.query) {
      return dispatch({
        type: Actions.LOAD,
        reqId,
        items: [],
      })
    }

    debouncedSearch(state.query, reqId)

    return () => debouncedSearch.cancel()
  }, [debouncedSearch, state.query])

  return {
    ...state,
    reload: () => debouncedSearch(state.query, Date.now()),
    run: (query: string) =>
      dispatch({
        type: Actions.SEARCH,
        query,
        reqId: Date.now(),
      }),
  }
}
